import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { MetaResponse } from '@/common/types/metaResponse'
import { SupportTicketAvailableUser } from '@/features/support/internal/types/supportTicketAvailableUser.type'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'

type UseSupportAvailableUsersProps<T> = {
  filters?: {
    search?: string
  }
  mapper?: (data: any) => T
}

export const useSupportAvailableUsers = <T,>({
  filters: { search = '' } = {},
  mapper = Mapper.default<T>(),
}: UseSupportAvailableUsersProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { data: { data, meta } = defaultTransformerResponse<T>(), ...rest } = useInfiniteQuery({
    queryKey: ['supportAvailableUsers', search],
    queryFn: ({ pageParam }) => {
      return privateAxios.get('/support/tickets/available_users', {
        params: {
          search,
          page: pageParam,
        },
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: { data: { data: any[]; meta: MetaResponse } }) => {
      if (lastPage?.data?.meta?.current_page < lastPage?.data?.meta?.total_pages) {
        return lastPage.data.meta.current_page + 1
      }
    },
    select: (data) => {
      return {
        data: mapper(
          data?.pages?.reduce((acc, page) => {
            return [...acc, ...page?.data?.data]
          }, [] as SupportTicketAvailableUser[]),
        ),
        meta: data?.pages[data?.pages?.length - 1].data.meta as MetaResponse,
      }
    },
    placeholderData: keepPreviousData,
  })

  return { data, meta, ...rest }
}
